import { intersectionWith, startsWith } from 'lodash';
import {
	ClassNameValue,
	extendTailwindMerge,
	// This is the only place we should use `twJoin` instead of `cj`.
	// eslint-disable-next-line no-restricted-imports
	twJoin,
} from 'tailwind-merge';

type AdditionalClassGroupIDs = 'typo' | 'scroll';

const TYPO_SUFFIXES = [
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	't1',
	't2',
	't3',
	't4',
	'b1',
	'b2',
	'b3',
	'b4',
	'b5',
];

const twMerge = extendTailwindMerge<AdditionalClassGroupIDs>({
	extend: {
		theme: {
			breakpoint: ['xs'],
			font: ['inter'],
			text: ['3xs', '2xs'],
			leading: [
				'2xs',
				'xs',
				'sm',
				'md',
				'lg',
				'xl',
				'2xl',
				'3xl',
				'4xl',
				'5xl',
				'6xl',
				'7xl',
			],
			spacing: ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'],
			radius: ['2xs'],
			shadow: ['reverse-sm', 'reverse-md', 'reverse-lg'],
		},
		classGroups: {
			// Ensures all typo classes override eachother.
			typo: [...TYPO_SUFFIXES.map((suffix) => `typo-${suffix}`)],
			scroll: ['thin-scroll', 'hidden-scroll'],
		},
		conflictingClassGroups: {
			// Ensures two things:
			//   1) `typo-b4 text-lg` results in `text-lg` overriding the font-size of `typo-b4`.
			//   2) `text-lg leading-lg font-bold typo-b4` results in `typo-b4` overriding the others.
			typo: ['font-size', 'font-weight', 'leading'],
		},
	},
});

/**
 * Should be used when we want to allow classname overrides.
 */
export function cm(...classLists: ClassNameValue[]) {
	return twMerge(...classLists);
}

/**
 * Should be used when we want to combine classnames without overrides.
 * Preferable to `cm` in terms of performance.
 */
export function cj(...classLists: ClassNameValue[]) {
	return twJoin(...classLists);
}

/**
 * We should be using `cm` to resolve classnames conflicts. Only use this
 * when you need to explicitly check for the presence of a classname and trigger
 * some logic based on it.
 */
export const hasClass = (classNames = '', ...withPrefixes: string[]) =>
	intersectionWith(classNames.split(' '), withPrefixes, startsWith).length > 0;
